const CARGOS = [
  'Diretor Regional De Unidade',
  'Diretor de Unidade',
  'Coordenador Pedagogico EI e EFI',
  'Coordenador Pedagogico EFII e EM',
  'Consultor de Relacionamento',
  'Secretaria Escolar',
  'Professor',
  'Atendimento',
  'Auxiliar de Coordenacao'
]

export function buildForumDashboard (source, classification = 'Todas') {
  const baseRows = source?.base || []
  const attendanceColumns = findAttendanceColumns(baseRows)
  const allAttendances = baseRows.slice(3)
    .filter(row => row[0] && row[1])
    .map(row => normalizeAttendance(row, attendanceColumns))
  const nameMap = buildNameMap(source?.collaborators || [])
  const capacityMap = buildCapacityMap(source?.capacityBase || [])
  const percentageMap = buildPercentageMap(source?.manualInputs || [])
  const blacklist = buildBlacklist(source?.manualInputs || [])
  const classifications = [...new Set(allAttendances.map(item => item.classification).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b))
  const attendances = classification === 'Todas'
    ? allAttendances
    : allAttendances.filter(item => item.classification === classification)

  return {
    week: String(baseRows[0]?.[1] || '').trim() || '(nenhuma semana selecionada)',
    classification,
    classifications,
    totalAttendances: attendances.length,
    byUnit: groupAttendancesByUnit(attendances),
    byCategory: groupBy(attendances, 'category', 'categoria'),
    byClassification: groupBy(attendances, 'classification', 'nome'),
    byArea: groupBy(attendances, 'area', 'nome'),
    capacity: buildCapacity(attendances, capacityMap, percentageMap, nameMap, blacklist, classification),
    duplicates: {
      students: buildStudentDuplicates(attendances),
      collaborators: buildCollaboratorDuplicates(attendances)
    },
    alerts: {
      excess: detectExcess(attendances, capacityMap, percentageMap, nameMap),
      blacklist: detectBlacklistViolations(attendances, blacklist, nameMap)
    },
    collaborators: assignedCollaborators(allAttendances, nameMap),
    attendances
  }
}

function findAttendanceColumns (rows) {
  const headers = rows.slice(0, 3)
    .map(row => row.map(normalizeLabel))
    .find(row => row.some(header => header.includes('unidade') && header.includes('atendido'))) || []
  const attendedUnitIndex = headers.findIndex(header => header.includes('unidade') && header.includes('atendido'))

  return {
    unit: attendedUnitIndex >= 0 ? attendedUnitIndex : 1
  }
}

function normalizeAttendance (row, columns) {
  return {
    date: formatDate(row[0]),
    unit: clean(row[columns.unit]).toUpperCase(),
    period: clean(row[2]),
    collaborator: clean(row[3]),
    financialResponsible: clean(row[4]),
    pedagogicalResponsible: clean(row[5]),
    student: clean(row[6]),
    className: clean(row[7]),
    observations: clean(row[8]),
    link: clean(row[9]),
    category: clean(row[10]),
    channel: clean(row[11]),
    assignedEmail: clean(row[12]).toLowerCase(),
    role: clean(row[13]),
    classification: clean(row[14]),
    area: clean(row[15])
  }
}

function buildNameMap (rows) {
  return rows.slice(1).reduce((map, row) => {
    const email = clean(row[0]).toLowerCase()
    const name = clean(row[1])
    if (email && name) map[email] = name
    return map
  }, {})
}

function buildCapacityMap (rows) {
  const headerIndex = rows.findIndex(row => {
    const headers = row.map(normalizeLabel)
    return ['classificacao', 'unidade', 'cargo', 'capacidade'].every(header => headers.includes(header))
  })

  if (headerIndex >= 0) {
    const headers = rows[headerIndex].map(normalizeLabel)
    const classificationColumn = headers.indexOf('classificacao')
    const unitColumn = headers.indexOf('unidade')
    const roleColumn = headers.indexOf('cargo')
    const capacityColumn = headers.indexOf('capacidade')

    return rows.slice(headerIndex + 1).reduce((map, row) => {
      const classification = clean(row[classificationColumn])
      const unit = clean(row[unitColumn]).toUpperCase()
      const roleIndex = findRoleIndex(row[roleColumn])
      const capacity = parseNumber(row[capacityColumn])

      if (!classification || !/^[A-Z]{2,5}$/.test(unit) || roleIndex < 0 || capacity <= 0) return map

      const key = `${classification}|${unit}`
      if (!map[key]) map[key] = new Array(CARGOS.length).fill(0)
      map[key][roleIndex] += capacity
      return map
    }, {})
  }

  return buildLegacyCapacityMap(rows)
}

function buildLegacyCapacityMap (rows) {
  const result = {}
  let currentClassification = ''
  let roleColumns = {}
  let readingBlock = false

  rows.forEach(row => {
    if (row.every(value => value === '' || value === null || value === undefined)) {
      readingBlock = false
      return
    }

    const columnB = clean(row[1])
    const columnC = clean(row[2])
    if (columnB && columnC && findCapacityRoleIndex(columnC, 6) >= 0) {
      currentClassification = columnB
      roleColumns = {}
      readingBlock = true
      row.forEach((value, columnIndex) => {
        if (columnIndex <= 1) return
        const roleIndex = findCapacityRoleIndex(value, 8)
        if (roleIndex >= 0) roleColumns[columnIndex] = roleIndex
      })
      return
    }

    if (readingBlock && columnB && /^[A-Z]{2,5}$/.test(columnB)) {
      const key = `${currentClassification}|${columnB}`
      if (!result[key]) result[key] = new Array(CARGOS.length).fill(0)
      Object.entries(roleColumns).forEach(([columnIndex, roleIndex]) => {
        result[key][roleIndex] += parseNumber(row[columnIndex])
      })
    }
  })

  return result
}

function buildPercentageMap (rows) {
  const headerIndex = rows.findIndex(row => normalizeLabel(row[0]) === 'unidade' && row.slice(1).some(value => findRoleIndex(value) >= 0))

  if (headerIndex < 0) return buildLegacyPercentageMap(rows)

  const roleColumns = {}
  rows[headerIndex].forEach((value, columnIndex) => {
    if (columnIndex === 0) return
    const roleIndex = findRoleIndex(value)
    if (roleIndex >= 0) roleColumns[columnIndex] = roleIndex
  })

  return rows.slice(headerIndex + 1).reduce((map, row) => {
    const unit = clean(row[0])
    if (/^[A-Z]{2,5}$/.test(unit)) {
      map[unit] = new Array(CARGOS.length).fill(1)
      Object.entries(roleColumns).forEach(([columnIndex, roleIndex]) => {
        map[unit][roleIndex] = parsePercentage(row[columnIndex])
      })
    }
    return map
  }, {})
}

function buildLegacyPercentageMap (rows) {
  return rows.slice(6, 36).reduce((map, row) => {
    const unit = clean(row[0])
    if (/^[A-Z]{2,5}$/.test(unit)) map[unit] = row.slice(1, CARGOS.length + 1).map(parsePercentage)
    return map
  }, {})
}

function buildBlacklist (rows) {
  const headerIndex = rows.findIndex(row => clean(row[0]) === 'Semana' && clean(row[1]) === 'Colaborador')
  if (headerIndex < 0) return []

  return rows.slice(headerIndex + 1, headerIndex + 26)
    .filter(row => clean(row[1]))
    .map(row => ({
      week: clean(row[0]),
      collaborator: clean(row[1]),
      unit: clean(row[2]),
      role: clean(row[3]),
      requester: clean(row[4]),
      observations: clean(row[5])
    }))
}

function groupBy (attendances, field, outputName) {
  const grouped = attendances.reduce((map, item) => {
    const key = item[field]
    if (key) map[key] = (map[key] || 0) + 1
    return map
  }, {})

  return Object.entries(grouped)
    .map(([name, total]) => ({ [outputName]: name, nome: name, total }))
    .sort((a, b) => b.total - a.total)
}

function groupAttendancesByUnit (attendances) {
  const grouped = attendances.reduce((map, item) => {
    if (!item.unit) return map
    if (!map[item.unit]) {
      map[item.unit] = {
        unidade: item.unit,
        rotinas: 0,
        registros: 0,
        intervencoes: 0,
        total: 0
      }
    }

    map[item.unit].total++
    const classification = classificationColumn(item.classification)
    if (classification) map[item.unit][classification]++
    return map
  }, {})

  return Object.values(grouped).sort((a, b) => b.total - a.total)
}

function classificationColumn (value) {
  const classification = clean(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()

  if (classification.startsWith('rotina')) return 'rotinas'
  if (classification.startsWith('registro')) return 'registros'
  if (classification.startsWith('intervenc')) return 'intervencoes'
  return ''
}

function detectExcess (attendances, capacityMap, percentageMap, nameMap) {
  const counts = {}
  attendances.forEach(item => {
    const roleIndex = findAttendanceRoleIndex(item.role)
    if (!item.classification || !item.unit || !item.assignedEmail || roleIndex < 0) return
    const key = `${item.classification}|${item.unit}|${roleIndex}|${item.assignedEmail}`
    counts[key] = (counts[key] || 0) + 1
  })

  return Object.entries(counts).flatMap(([key, count]) => {
    const [classification, unit, roleIndexValue, email] = key.split('|')
    const roleIndex = Number(roleIndexValue)
    const base = capacityMap[`${classification}|${unit}`]
    const baseCapacity = base?.[roleIndex] || 0
    if (!baseCapacity) return []
    const capacity = Math.round(baseCapacity * (percentageMap[unit]?.[roleIndex] ?? 1))
    if (count <= capacity) return []
    return [{ classification, unit, role: CARGOS[roleIndex], collaborator: nameMap[email] || email, email, submitted: count, capacity, excess: count - capacity }]
  }).sort((a, b) => b.excess - a.excess)
}

function detectBlacklistViolations (attendances, blacklist, nameMap) {
  const areasByEmail = attendances.reduce((map, item) => {
    if (!item.assignedEmail) return map
    if (!map[item.assignedEmail]) map[item.assignedEmail] = {}
    if (item.area) map[item.assignedEmail][item.area] = (map[item.assignedEmail][item.area] || 0) + 1
    return map
  }, {})

  return blacklist.flatMap(item => {
    const email = Object.keys(nameMap).find(key => nameMap[key] === item.collaborator)
    if (!email || !areasByEmail[email]) return []
    const areas = Object.entries(areasByEmail[email])
      .sort((a, b) => b[1] - a[1])
      .map(([area, count]) => ({ area, count }))
    return [{ ...item, email, areas }]
  })
}

function buildCapacity (attendances, capacityMap, percentageMap, nameMap, blacklist, classification) {
  const blacklistNames = new Set(blacklist.map(item => item.collaborator))
  const tree = {}

  attendances.forEach(item => {
    const roleIndex = findAttendanceRoleIndex(item.role)
    if (!item.unit || !item.assignedEmail || roleIndex < 0) return
    if (!tree[item.unit]) tree[item.unit] = {}
    if (!tree[item.unit][roleIndex]) tree[item.unit][roleIndex] = {}
    if (!tree[item.unit][roleIndex][item.assignedEmail]) {
      tree[item.unit][roleIndex][item.assignedEmail] = { count: 0, areas: {}, items: [] }
    }
    const collaborator = tree[item.unit][roleIndex][item.assignedEmail]
    collaborator.count++
    collaborator.items.push(item)
    if (item.area) collaborator.areas[item.area] = (collaborator.areas[item.area] || 0) + 1
  })

  const units = Object.keys(tree).sort().map(unit => {
    const roles = Object.keys(tree[unit]).map(Number).sort((a, b) => a - b).map(roleIndex => {
      const baseCapacity = Object.entries(capacityMap).reduce((total, [key, values]) => {
        if (!key.endsWith(`|${unit}`)) return total
        if (classification !== 'Todas' && !key.startsWith(`${classification}|`)) return total
        return total + (values[roleIndex] || 0)
      }, 0)
      const hasCapacity = baseCapacity > 0
      const weeklyCapacity = Math.round(baseCapacity * (percentageMap[unit]?.[roleIndex] ?? 1))
      const collaborators = Object.entries(tree[unit][roleIndex]).map(([email, item]) => {
        const excess = hasCapacity ? Math.max(0, item.count - weeklyCapacity) : null

        return {
          email,
          name: nameMap[email] || email,
          attendances: item.count,
          attendanceItems: item.items,
          areas: item.areas,
          hasCapacity,
          weeklyCapacity,
          excess,
          isBlacklist: blacklistNames.has(nameMap[email] || email)
        }
      }).sort((a, b) => b.attendances - a.attendances)
      const totalAttendances = collaborators.reduce((total, item) => total + item.attendances, 0)
      const totalCapacity = hasCapacity ? weeklyCapacity * collaborators.length : null
      const excess = hasCapacity ? collaborators.reduce((total, item) => total + item.excess, 0) : null

      return {
        name: CARGOS[roleIndex],
        totalAttendances,
        hasCapacity,
        weeklyCapacity,
        totalCapacity,
        excess,
        collaborators
      }
    })
    const total = roles.reduce((sum, role) => sum + role.totalAttendances, 0)
    const hasCapacity = roles.some(role => role.hasCapacity)
    const excess = roles.reduce((sum, role) => sum + (role.hasCapacity ? Math.max(0, role.excess) : 0), 0)
    return { acronym: unit, total, hasCapacity, excess, roles }
  })

  return { units }
}

function buildStudentDuplicates (attendances) {
  const grouped = groupDuplicates(attendances, 'student', item => ({
    category: item.category,
    collaborator: item.collaborator,
    area: item.area,
    classification: item.classification
  }))
  return grouped.map(item => ({ ...item, unit: item.first.unit, className: item.first.className }))
}

function buildCollaboratorDuplicates (attendances) {
  const grouped = groupDuplicates(attendances, 'collaborator', item => ({
    category: item.category,
    area: item.area,
    classification: item.classification
  }))
  return grouped.map(item => ({ ...item, unit: item.first.unit, role: item.first.role }))
}

function groupDuplicates (attendances, field, detailMapper) {
  const grouped = attendances.reduce((map, item) => {
    const name = item[field]
    if (!name) return map
    if (!map[name]) map[name] = { name, first: item, attendances: [] }
    map[name].attendances.push(detailMapper(item))
    return map
  }, {})

  return Object.values(grouped)
    .filter(item => item.attendances.length > 1)
    .sort((a, b) => b.attendances.length - a.attendances.length)
}

function assignedCollaborators (attendances, nameMap) {
  const collaborators = new Map()
  attendances.forEach(item => {
    if (!item.assignedEmail) return
    collaborators.set(item.assignedEmail, {
      email: item.assignedEmail,
      name: nameMap[item.assignedEmail] || item.assignedEmail
    })
  })
  return [...collaborators.values()]
}

function findAttendanceRoleIndex (value) {
  return findRoleIndex(value)
}

function findCapacityRoleIndex (value, prefixLength) {
  const roleName = normalizeCapacityRole(value).slice(0, prefixLength)
  return CARGOS.findIndex(role => normalizeCapacityRole(role).slice(0, prefixLength) === roleName)
}

function normalizeCapacityRole (value) {
  return clean(value).toLowerCase().replace(/\s/g, '')
}

function findRoleIndex (value) {
  const roleName = normalizeLabel(value)
  const aliases = {
    coordenadorpedagogicoeiefi: 2,
    coordenadorpedagogicoffeem: 3,
    coordenadorpedagogicoefiieem: 3
  }

  if (Object.prototype.hasOwnProperty.call(aliases, roleName)) return aliases[roleName]
  return CARGOS.findIndex(role => normalizeLabel(role) === roleName)
}

function normalizeLabel (value) {
  return clean(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
}

function parsePercentage (value) {
  const text = clean(value)
  if (!text) return 1
  if (text.includes('%')) return parseNumber(text.replace('%', '')) / 100
  const number = parseNumber(text)
  return number > 1 ? number / 100 : number
}

function parseNumber (value) {
  const number = Number(String(value ?? '').replace(',', '.'))
  return Number.isFinite(number) ? number : 0
}

function clean (value) {
  return String(value ?? '').trim()
}

function formatDate (value) {
  return clean(value).slice(0, 10)
}
